/**
 * Call.ts
 * 
 * Handles API communication with the requirements linting service. Provides functionality
 * for validating and evaluating requirements text through HTTP requests, with retry logic
 * and state management.
 */
/*! Copyright Jon Verrier 2025 */

import axios from 'axios';
import axiosRetry from 'axios-retry';

import { IQuickCheckRequest, 
   IQuickCheckResponse, 
   ISpecificationEvaluationRequest, 
   ISpecificationEvaluation } from '../import/RequirementsLinterApiTypes';
import { EApiEvent } from './UIStateMachine';

interface ApiClient {
    post: <T>(url: string, data: any) => Promise<{ data: T }>;
}

/**
 * Options for processing a requirement through the linting service.
 * 
 * This interface defines the parameters required for processing a requirement
 * through the linting service. It includes the URLs for the quick check and
 * evaluation endpoints, the requirement text to process, a session ID for tracking,
 * and a callback function to update the UI state.
 */
interface ProcessRequirementOptions {
    quickCheckUrl: string;
    evaluationUrl: string;
    specification: string;
    sessionId: string;
    updateState: (event: EApiEvent) => void;
    apiClient?: ApiClient;
}

/**
 * Processes a requirement through the linting service.
 * 
 * This function orchestrates the validation and evaluation of a requirement text
 * through the requirements linting service. It performs a quick check to ensure
 * the requirement is valid, and then performs a full evaluation if the requirement
 * passes the quick check.
 * 
 * @param options - The options for processing the requirement.
 * @returns The evaluation result if successful, or undefined if the requirement is rejected.
 */
export async function processRequirement({
    quickCheckUrl,
    evaluationUrl,
    specification,
    sessionId,
    updateState,
    apiClient: apiClientIn
}: ProcessRequirementOptions): Promise<ISpecificationEvaluation | undefined> {

    let apiClient = apiClientIn;
    if (!apiClient) {
      const client = axios.create();
      axiosRetry(client, {
          retries: 3,
          retryDelay: axiosRetry.exponentialDelay,
          retryCondition: (error) => {
              // Retry on network errors or 5xx server errors
              return axiosRetry.isNetworkOrIdempotentRequestError(error) ||
                     (error.response?.status ?? 0) >= 500;
          }
      });
      apiClient = client;
    }

    try {
        // Signal start of quick check
        updateState(EApiEvent.StartedCheck);

        // First API call - Quick Check
        const quickCheckRequest: IQuickCheckRequest = {
            statement: specification,
            beFriendly: true,
            sessionId: sessionId
        };

        const quickCheckResponse = await apiClient.post<IQuickCheckResponse>(
            quickCheckUrl,
            quickCheckRequest
        );

        if (!quickCheckResponse.data.isSpecification) {
            updateState(EApiEvent.RejectedCheck);
            return undefined;
        }

        // If it is a requirement, signal passing check and start linting
        updateState(EApiEvent.PassedCheck);
        updateState(EApiEvent.StartedLint);

        // Second API call - Requirement Evaluation
        const evaluationRequest: ISpecificationEvaluationRequest = {
            specification: specification,
            sessionId: sessionId
        };

        const evaluationResponse = await apiClient.post<ISpecificationEvaluation>(
            evaluationUrl,
            evaluationRequest
        );

        // Signal completion of linting
        updateState(EApiEvent.Linted);

        return evaluationResponse.data;

    } catch (error: unknown) {
        // Handle any errors that weren't automatically retried or failed after retries
        updateState(EApiEvent.Error);

        if (axios.isAxiosError(error)) {
            console.error('API Error:', error.response?.data || error.message);
        } else {
            console.error('Unexpected error:', error instanceof Error ? error.message : String(error));
        }

        return undefined;
    }
}